// src/pages/CustomerPriceSetting.js
import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  MdEdit,
  MdDelete,
  MdAdd,
  MdSearch,
  MdClear,
  MdFilterList,
  MdRefresh,
  MdVisibility,
  MdFileDownload,
  MdDirectionsTransit,
  MdLocationOn,
  MdAttachMoney,
  MdCheckCircle,
  MdCancel,
  MdInfoOutline
} from 'react-icons/md';
import {
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineCurrencyRupee,
  HiOutlineScale,
  HiOutlineTruck
} from 'react-icons/hi';
import { FaTimes, FaExchangeAlt, FaBoxes, FaShieldAlt } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const MODES = [
  { value: 1, label: 'Surface' },
  { value: 2, label: 'Air' },
  { value: 3, label: 'Rail' },
  { value: 4, label: 'Water' },
  { value: 5, label: 'Multimodal' },
];

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    borderRadius: '0.5rem',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
    '&:hover': { borderColor: '#2563eb' },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const initialForm = {
  customerId: '',
  fromStateId: '',
  toStateId: '',
  fromLocationId: [],
  toLocationId: [],
  isMetro: 0,
  modeId: 1,
  isOda: 0,
  ratePerKg: '',
  adRate: '',
  pickupOda: '',
  deliveryOda: '',
  minAmount: '',
  minKgs: '',
  maxKgs: '',
  minChrgWeight: '',
  codChrg: '',
  iata: '',
  cft: '',
  docChrg: '',
  fovPercent: '',
  hamaliChrg: '',
  fuelChrg: '',
  loadingCharge: '',
  unloadingCharge: '',
  otherCharge: '',
  coCourier: '',
  fod: '',
  adjWeight: '',
  holidayCharges: '',
  specialCharges: '',
  flatAmount: '',
  pickupCharge: '',
  deliveryCharge: '',
  freightAmount: '',
  finalBreakup: '',
  volume: '',
  priceBox: '',
  status: true,
};

const CustomerPriceSetting = () => {
  const [records, setRecords] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [states, setStates] = useState([]);
  const [fromAreas, setFromAreas] = useState([]);
  const [toAreas, setToAreas] = useState([]);
  const [loading, setLoading] = useState(false);

  // View state: 'list' or 'form'
  const [showList, setShowList] = useState(true);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [editingId, setEditingId] = useState(null);
  const [viewDetailRecord, setViewDetailRecord] = useState(null);

  // Filters & Search
  const [search, setSearch] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterFromState, setFilterFromState] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  // Form area searches
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');

  // Form State
  const [form, setForm] = useState(initialForm);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // ==================== FETCH DATA ====================
  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/customer`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch customers');
      const { data } = await res.json();
      setCustomers(Array.isArray(data) ? data.map(c => ({ _id: c._id, name: c.name || c.companyName })) : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStates = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/state`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch states');
      const { data } = await res.json();
      setStates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAreas = async (stateId) => {
    if (!stateId) return [];
    try {
      const res = await fetch(`${API_BASE_URL}/area?stateid=${stateId}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch areas');
      const { data } = await res.json();
      return Array.isArray(data)
        ? data.map(area => ({
            ...area,
            displayName: `${area.areaname} - ${area.pincode?.pincode || 'N/A'}`
          }))
        : [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/customerpricesetting`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch records');
      const { data } = await res.json();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error fetching records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchCustomers(), fetchStates(), fetchRecords()]);
    };
    if (localStorage.getItem('token')) load();
  }, []);

  // Load areas when state changes in form
  useEffect(() => {
    if (form.fromStateId) {
      fetchAreas(form.fromStateId).then(setFromAreas);
    } else {
      setFromAreas([]);
      setForm(prev => ({ ...prev, fromLocationId: [] }));
    }
  }, [form.fromStateId]);

  useEffect(() => {
    if (form.toStateId) {
      fetchAreas(form.toStateId).then(setToAreas);
    } else {
      setToAreas([]);
      setForm(prev => ({ ...prev, toLocationId: [] }));
    }
  }, [form.toStateId]);

  // Filtered Area Checkbox Lists
  const filteredFromAreas = useMemo(() => {
    return fromAreas.filter(area =>
      area.displayName.toLowerCase().includes(fromSearch.toLowerCase())
    );
  }, [fromAreas, fromSearch]);

  const filteredToAreas = useMemo(() => {
    return toAreas.filter(area =>
      area.displayName.toLowerCase().includes(toSearch.toLowerCase())
    );
  }, [toAreas, toSearch]);

  const handleSelectAllAreas = (type, itemsToToggle) => {
    const fieldName = type === 'from' ? 'fromLocationId' : 'toLocationId';
    const currentIds = form[fieldName] || [];
    const targetIds = itemsToToggle.map(item => item._id);

    const allSelected = targetIds.length > 0 && targetIds.every(id => currentIds.includes(id));

    if (allSelected) {
      setForm(prev => ({
        ...prev,
        [fieldName]: (prev[fieldName] || []).filter(id => !targetIds.includes(id))
      }));
    } else {
      setForm(prev => ({
        ...prev,
        [fieldName]: [...new Set([...(prev[fieldName] || []), ...targetIds])]
      }));
    }
  };

  // ==================== CRUD ====================
  const createRecord = async (data) => {
    const res = await fetch(`${API_BASE_URL}/customerpricesetting`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to create price setting');
    }
    return res.json();
  };

  const updateRecord = async (id, data) => {
    const res = await fetch(`${API_BASE_URL}/customerpricesetting/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update price setting');
    }
    return res.json();
  };

  const deleteRecord = async (id) => {
    const res = await fetch(`${API_BASE_URL}/customerpricesetting/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete record');
    }
    return res.json();
  };

  // ==================== FORM HANDLERS ====================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setForm(prev => {
        const arr = prev[name] || [];
        if (checked) {
          return { ...prev, [name]: [...arr, value] };
        } else {
          return { ...prev, [name]: arr.filter(v => v !== value) };
        }
      });
    } else if (type === 'number') {
      setForm(prev => ({ ...prev, [name]: value === '' ? '' : parseFloat(value) || 0 }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setFromAreas([]);
    setToAreas([]);
    setFromSearch('');
    setToSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.customerId || !form.fromStateId || !form.toStateId) {
      toast.error('Please select Customer, From State, and To State');
      return;
    }

    if (!form.fromLocationId || form.fromLocationId.length === 0 || !form.toLocationId || form.toLocationId.length === 0) {
      toast.error('Please select at least one From Location and one To Location');
      return;
    }

    try {
      setLoading(true);
      if (editingId) {
        await updateRecord(editingId, form);
        toast.success('Price setting updated successfully!');
      } else {
        await createRecord(form);
        toast.success('Price setting created successfully!');
      }
      resetForm();
      setShowList(true);
      fetchRecords();
    } catch (err) {
      toast.error(err.message || 'Error saving rate record');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (record) => {
    const fromStateId = record.fromStateId?._id || record.fromStateId || '';
    const toStateId = record.toStateId?._id || record.toStateId || '';

    const [loadedFromAreas, loadedToAreas] = await Promise.all([
      fetchAreas(fromStateId),
      fetchAreas(toStateId)
    ]);

    setFromAreas(loadedFromAreas);
    setToAreas(loadedToAreas);

    const fromLocIds = Array.isArray(record.fromLocationId)
      ? record.fromLocationId.map(loc => (typeof loc === 'object' ? loc._id : loc))
      : [];

    const toLocIds = Array.isArray(record.toLocationId)
      ? record.toLocationId.map(loc => (typeof loc === 'object' ? loc._id : loc))
      : [];

    setForm({
      customerId: record.customerId?._id || record.customerId || '',
      fromStateId,
      toStateId,
      fromLocationId: fromLocIds,
      toLocationId: toLocIds,
      isMetro: record.isMetro || 0,
      modeId: record.modeId || 1,
      isOda: record.isOda || 0,
      ratePerKg: record.ratePerKg ?? '',
      adRate: record.adRate ?? '',
      pickupOda: record.pickupOda ?? '',
      deliveryOda: record.deliveryOda ?? '',
      minAmount: record.minAmount ?? '',
      minKgs: record.minKgs ?? '',
      maxKgs: record.maxKgs ?? '',
      minChrgWeight: record.minChrgWeight ?? '',
      codChrg: record.codChrg ?? '',
      iata: record.iata ?? '',
      cft: record.cft ?? '',
      docChrg: record.docChrg ?? '',
      fovPercent: record.fovPercent ?? '',
      hamaliChrg: record.hamaliChrg ?? '',
      fuelChrg: record.fuelChrg ?? '',
      loadingCharge: record.loadingCharge ?? '',
      unloadingCharge: record.unloadingCharge ?? '',
      otherCharge: record.otherCharge ?? '',
      coCourier: record.coCourier ?? '',
      fod: record.fod ?? '',
      adjWeight: record.adjWeight ?? '',
      holidayCharges: record.holidayCharges ?? '',
      specialCharges: record.specialCharges ?? '',
      flatAmount: record.flatAmount ?? '',
      pickupCharge: record.pickupCharge ?? '',
      deliveryCharge: record.deliveryCharge ?? '',
      freightAmount: record.freightAmount ?? '',
      finalBreakup: record.finalBreakup ?? '',
      volume: record.volume ?? '',
      priceBox: record.priceBox ?? '',
      status: record.status !== false,
    });

    setEditingId(record._id);
    setShowList(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, custName) => {
    const result = await Swal.fire({
      title: 'Delete Price Setting?',
      text: `Are you sure you want to remove pricing for "${custName || 'this route'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      borderRadius: '0.75rem',
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await deleteRecord(id);
        toast.success('Rate record deleted successfully');
        fetchRecords();
      } catch (err) {
        toast.error(err.message || 'Error deleting record');
      } finally {
        setLoading(false);
      }
    }
  };

  // Helper lookup
  const getCustomerName = (r) => {
    const cId = r.customerId?._id || r.customerId;
    const match = customers.find(c => c._id === cId);
    return match ? match.name : (r.customerId?.name || r.customerId?.companyName || 'N/A');
  };

  const getStateName = (stateRef) => {
    const sId = stateRef?._id || stateRef;
    const match = states.find(s => s._id === sId);
    return match ? match.statename : (stateRef?.statename || 'N/A');
  };

  const getModeLabel = (modeId) => {
    const match = MODES.find(m => m.value === Number(modeId));
    return match ? match.label : 'Surface';
  };

  // ==================== FILTER & PAGINATION ====================
  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return records.filter(r => {
      const cust = getCustomerName(r).toLowerCase();
      const fromSt = getStateName(r.fromStateId).toLowerCase();
      const toSt = getStateName(r.toStateId).toLowerCase();
      const rateStr = String(r.ratePerKg || '');

      const matchesSearch = !term ||
        cust.includes(term) ||
        fromSt.includes(term) ||
        toSt.includes(term) ||
        rateStr.includes(term);

      const custId = r.customerId?._id || r.customerId;
      const matchesCust = !filterCustomer || custId === filterCustomer;

      const fromId = r.fromStateId?._id || r.fromStateId;
      const matchesFrom = !filterFromState || fromId === filterFromState;

      const matchesStatus =
        filterStatus === 'ALL' ||
        (filterStatus === 'ACTIVE' && r.status !== false) ||
        (filterStatus === 'INACTIVE' && r.status === false);

      return matchesSearch && matchesCust && matchesFrom && matchesStatus;
    });
  }, [records, search, filterCustomer, filterFromState, filterStatus, customers, states]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const resetFilters = () => {
    setSearch('');
    setFilterCustomer('');
    setFilterFromState('');
    setFilterStatus('ALL');
    setCurrentPage(1);
  };

  // KPI calculations
  const totalCount = records.length;
  const activeCount = records.filter(r => r.status !== false).length;
  const inactiveCount = records.filter(r => r.status === false).length;
  const coveredCustomersCount = new Set(records.map(r => r.customerId?._id || r.customerId)).size;

  // Excel Export
  const exportToExcel = () => {
    if (records.length === 0) {
      toast.warn('No records to export');
      return;
    }

    const exportData = filtered.map((r, i) => ({
      'S.No': i + 1,
      'Customer': getCustomerName(r),
      'From State': getStateName(r.fromStateId),
      'To State': getStateName(r.toStateId),
      'Mode': getModeLabel(r.modeId),
      'Rate/Kg (₹)': r.ratePerKg ?? '',
      'Min Amount (₹)': r.minAmount ?? '',
      'Min Kgs': r.minKgs ?? '',
      'Max Kgs': r.maxKgs ?? '',
      'Metro': r.isMetro ? 'Yes' : 'No',
      'ODA': r.isOda ? 'Yes' : 'No',
      'Pickup ODA (₹)': r.pickupOda ?? '',
      'Delivery ODA (₹)': r.deliveryOda ?? '',
      'Fuel Charge (₹)': r.fuelChrg ?? '',
      'Docket Charge (₹)': r.docChrg ?? '',
      'FOV %': r.fovPercent ?? '',
      'Status': r.status !== false ? 'Active' : 'Inactive',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customer Pricing');
    XLSX.writeFile(workbook, `Customer_Pricing_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Pricing report exported to Excel!');
  };

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Page Title & Breadcrumb Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
            <HiOutlineCurrencyRupee className="text-blue-600 text-3xl" />
            Customer Rate Master
          </h2>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Configure freight pricing matrices, per-kg rates, and route tariffs for customers
          </p>
        </div>

        <div className="flex items-center gap-2">
          {showList ? (
            <button
              onClick={() => {
                resetForm();
                setShowList(false);
              }}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow transition-all duration-150 flex items-center gap-2 text-sm uppercase tracking-wide cursor-pointer"
            >
              <MdAdd size={20} /> Add New Rate
            </button>
          ) : (
            <button
              onClick={() => {
                resetForm();
                setShowList(true);
              }}
              className="px-5 py-2.5 bg-gray-700 hover:bg-gray-800 text-white font-semibold rounded-lg shadow transition-all duration-150 flex items-center gap-2 text-sm uppercase tracking-wide cursor-pointer"
            >
              <HiOutlineViewList size={20} /> Back to Rate List
            </button>
          )}
        </div>
      </div>

      {/* KPI Summary Quick Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-2xl font-bold">
            <HiOutlineCurrencyRupee />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Rates</p>
            <p className="text-2xl font-extrabold text-gray-900">{totalCount}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-green-50 text-green-600 flex items-center justify-center text-2xl font-bold">
            <MdCheckCircle />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Active Rates</p>
            <p className="text-2xl font-extrabold text-green-600">{activeCount}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-red-50 text-red-600 flex items-center justify-center text-2xl font-bold">
            <MdCancel />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Inactive Rates</p>
            <p className="text-2xl font-extrabold text-red-600">{inactiveCount}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-2xl font-bold">
            <HiOutlineTruck />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Covered Clients</p>
            <p className="text-2xl font-extrabold text-indigo-600">{coveredCustomersCount}</p>
          </div>
        </div>
      </div>

      {/* MAIN CONTAINER */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6">
        {showList ? (
          <div>
            {/* Unified Control Toolbar */}
            <div className="flex flex-col gap-4 mb-6 pb-5 border-b border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Search Bar */}
                <div className="relative flex-1 max-w-lg">
                  <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  <input
                    type="text"
                    placeholder="Search by customer, state, rate per kg..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <MdClear size={18} />
                    </button>
                  )}
                </div>

                {/* View Switcher, Entries Per Page & Excel Export */}
                <div className="flex items-center flex-wrap gap-2.5">
                  {/* Entries per page */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">Show:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="py-2 px-2.5 bg-gray-50 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={6}>6 per page</option>
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                  </div>

                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200">
                    <button
                      type="button"
                      onClick={() => setViewMode('card')}
                      className={`p-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition cursor-pointer ${
                        viewMode === 'card'
                          ? 'bg-white text-blue-600 shadow-sm font-bold'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Landscape Card View"
                    >
                      <HiOutlineViewGrid size={18} />
                      <span className="hidden sm:inline">Cards</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('table')}
                      className={`p-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition cursor-pointer ${
                        viewMode === 'table'
                          ? 'bg-white text-blue-600 shadow-sm font-bold'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Table List View"
                    >
                      <HiOutlineViewList size={18} />
                      <span className="hidden sm:inline">Table</span>
                    </button>
                  </div>

                  {/* Export Excel Button */}
                  <button
                    onClick={exportToExcel}
                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm transition cursor-pointer"
                    title="Export data to Excel"
                  >
                    <MdFileDownload size={18} />
                    <span className="hidden sm:inline">Export Excel</span>
                  </button>
                </div>
              </div>

              {/* Filters Row */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <span className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
                  <MdFilterList /> Filters:
                </span>

                {/* Filter by Customer */}
                <select
                  value={filterCustomer}
                  onChange={(e) => {
                    setFilterCustomer(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="py-1.5 px-3 bg-gray-50 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[200px]"
                >
                  <option value="">All Customers</option>
                  {customers.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>

                {/* Filter by From State */}
                <select
                  value={filterFromState}
                  onChange={(e) => {
                    setFilterFromState(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="py-1.5 px-3 bg-gray-50 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[180px]"
                >
                  <option value="">All Origin States</option>
                  {states.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.statename}
                    </option>
                  ))}
                </select>

                {/* Filter by Status */}
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="py-1.5 px-3 bg-gray-50 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ALL">All Status</option>
                  <option value="ACTIVE">Active Only</option>
                  <option value="INACTIVE">Inactive Only</option>
                </select>

                {(search || filterCustomer || filterFromState || filterStatus !== 'ALL') && (
                  <button
                    onClick={resetFilters}
                    className="py-1.5 px-3 text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer"
                  >
                    <MdRefresh /> Reset Filters
                  </button>
                )}
              </div>
            </div>

            {/* CONTENT AREA: Landscape Cards vs Table */}
            {loading ? (
              <div className="text-center py-16 text-gray-500">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mb-3"></div>
                <p className="text-sm font-medium">Loading pricing records...</p>
              </div>
            ) : paginated.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                <HiOutlineCurrencyRupee className="mx-auto text-5xl text-gray-300 mb-2" />
                <p className="text-gray-700 font-bold text-base">No pricing records found</p>
                <p className="text-gray-500 text-xs mt-1">Try adjusting your search criteria or create a new rate matrix.</p>
                <button
                  onClick={() => {
                    resetForm();
                    setShowList(false);
                  }}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition"
                >
                  Add First Rate
                </button>
              </div>
            ) : viewMode === 'card' ? (
              /* LANDSCAPE CARD VIEW */
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-5">
                {paginated.map((r) => {
                  const custName = getCustomerName(r);
                  const fromState = getStateName(r.fromStateId);
                  const toState = getStateName(r.toStateId);
                  const modeName = getModeLabel(r.modeId);
                  const fromLocCount = Array.isArray(r.fromLocationId) ? r.fromLocationId.length : 0;
                  const toLocCount = Array.isArray(r.toLocationId) ? r.toLocationId.length : 0;

                  return (
                    <div
                      key={r._id}
                      className="bg-white rounded-xl border border-gray-200 hover:border-blue-400 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between overflow-hidden"
                    >
                      {/* Card Header */}
                      <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
                            ₹
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-sm font-bold text-gray-900 truncate" title={custName}>
                              {custName}
                            </h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded uppercase">
                                {modeName}
                              </span>
                              <span
                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                  r.status !== false
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {r.status !== false ? 'ACTIVE' : 'INACTIVE'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => setViewDetailRecord(r)}
                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-white rounded-lg transition"
                            title="View Details"
                          >
                            <MdVisibility size={18} />
                          </button>
                          <button
                            onClick={() => handleEdit(r)}
                            className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-white rounded-lg transition"
                            title="Edit Rate"
                          >
                            <MdEdit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(r._id, custName)}
                            className="p-1.5 text-red-600 hover:text-red-800 hover:bg-white rounded-lg transition"
                            title="Delete Rate"
                          >
                            <MdDelete size={18} />
                          </button>
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="p-5 space-y-4">
                        {/* Route Path visual */}
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-bold uppercase text-gray-400">Origin State</p>
                            <p className="text-xs font-bold text-gray-800 truncate" title={fromState}>
                              {fromState}
                            </p>
                            <p className="text-[10px] text-gray-500 mt-0.5">
                              {fromLocCount} {fromLocCount === 1 ? 'Location' : 'Locations'}
                            </p>
                          </div>

                          <div className="flex flex-col items-center justify-center shrink-0 px-2 text-blue-600">
                            <FaExchangeAlt size={14} className="text-blue-500" />
                          </div>

                          <div className="flex-1 min-w-0 text-right">
                            <p className="text-[10px] font-bold uppercase text-gray-400">Destination State</p>
                            <p className="text-xs font-bold text-gray-800 truncate" title={toState}>
                              {toState}
                            </p>
                            <p className="text-[10px] text-gray-500 mt-0.5">
                              {toLocCount} {toLocCount === 1 ? 'Location' : 'Locations'}
                            </p>
                          </div>
                        </div>

                        {/* Pricing Highlight Metrics */}
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="bg-emerald-50 rounded-lg p-2.5 border border-emerald-100">
                            <p className="text-[10px] uppercase font-bold text-emerald-700">Rate / Kg</p>
                            <p className="text-base font-extrabold text-emerald-800 mt-0.5">
                              ₹{r.ratePerKg || 0}
                            </p>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-100">
                            <p className="text-[10px] uppercase font-bold text-blue-700">Min Charge</p>
                            <p className="text-base font-extrabold text-blue-800 mt-0.5">
                              ₹{r.minAmount || 0}
                            </p>
                          </div>
                          <div className="bg-indigo-50 rounded-lg p-2.5 border border-indigo-100">
                            <p className="text-[10px] uppercase font-bold text-indigo-700">Weight Range</p>
                            <p className="text-xs font-bold text-indigo-900 mt-1">
                              {r.minKgs || 0} - {r.maxKgs || '∞'} kg
                            </p>
                          </div>
                        </div>

                        {/* Additional Attributes Badges */}
                        <div className="flex flex-wrap items-center gap-1.5 pt-1 text-[11px]">
                          {r.isMetro ? (
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-semibold">
                              Metro Route
                            </span>
                          ) : null}
                          {r.isOda ? (
                            <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold">
                              ODA Route
                            </span>
                          ) : null}
                          {r.pickupOda ? (
                            <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              Pick ODA: ₹{r.pickupOda}
                            </span>
                          ) : null}
                          {r.deliveryOda ? (
                            <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              Del ODA: ₹{r.deliveryOda}
                            </span>
                          ) : null}
                          {r.fuelChrg ? (
                            <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              Fuel: ₹{r.fuelChrg}
                            </span>
                          ) : null}
                          {r.docChrg ? (
                            <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              Doc: ₹{r.docChrg}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {/* Card Footer */}
                      <div className="bg-gray-50 px-5 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                        <span>Created Tariff Record</span>
                        <button
                          onClick={() => setViewDetailRecord(r)}
                          className="text-blue-600 hover:text-blue-800 font-semibold cursor-pointer"
                        >
                          Full Breakdown &rarr;
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* TABLE LIST VIEW */
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full table-auto border-collapse">
                  <thead className="bg-blue-600 text-white uppercase text-xs font-semibold tracking-wider">
                    <tr>
                      <th className="p-3.5 text-left">S.No</th>
                      <th className="p-3.5 text-left">Customer</th>
                      <th className="p-3.5 text-left">From State</th>
                      <th className="p-3.5 text-left">To State</th>
                      <th className="p-3.5 text-left">Mode</th>
                      <th className="p-3.5 text-left">Rate / Kg</th>
                      <th className="p-3.5 text-left">Min Amount</th>
                      <th className="p-3.5 text-left">Route Type</th>
                      <th className="p-3.5 text-left">Status</th>
                      <th className="p-3.5 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-xs">
                    {paginated.map((r, i) => {
                      const sNo = (currentPage - 1) * pageSize + i + 1;
                      const custName = getCustomerName(r);
                      const fromState = getStateName(r.fromStateId);
                      const toState = getStateName(r.toStateId);
                      const modeName = getModeLabel(r.modeId);

                      return (
                        <tr key={r._id} className="hover:bg-blue-50/50 transition">
                          <td className="p-3.5 font-bold text-gray-500">{sNo}</td>
                          <td className="p-3.5 font-bold text-gray-900">{custName}</td>
                          <td className="p-3.5 text-gray-700">{fromState}</td>
                          <td className="p-3.5 text-gray-700">{toState}</td>
                          <td className="p-3.5">
                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                              {modeName}
                            </span>
                          </td>
                          <td className="p-3.5 font-extrabold text-emerald-600 text-sm">
                            ₹{r.ratePerKg || 0}
                          </td>
                          <td className="p-3.5 font-semibold text-gray-800">
                            ₹{r.minAmount || 0}
                          </td>
                          <td className="p-3.5">
                            <div className="flex gap-1">
                              {r.isMetro ? (
                                <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-[10px] font-bold">
                                  Metro
                                </span>
                              ) : null}
                              {r.isOda ? (
                                <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-bold">
                                  ODA
                                </span>
                              ) : null}
                              {!r.isMetro && !r.isOda && (
                                <span className="text-gray-400 text-[11px]">-</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3.5">
                            <span
                              className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                                r.status !== false
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {r.status !== false ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td className="p-3.5 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => setViewDetailRecord(r)}
                                className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                title="View Details"
                              >
                                <MdVisibility size={18} />
                              </button>
                              <button
                                onClick={() => handleEdit(r)}
                                className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition"
                                title="Edit Rate"
                              >
                                <MdEdit size={18} />
                              </button>
                              <button
                                onClick={() => handleDelete(r._id, custName)}
                                className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition"
                                title="Delete Rate"
                              >
                                <MdDelete size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {filtered.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200 text-xs text-gray-600">
                <div>
                  Showing{' '}
                  <span className="font-bold text-gray-800">
                    {(currentPage - 1) * pageSize + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-bold text-gray-800">
                    {Math.min(currentPage * pageSize, filtered.length)}
                  </span>{' '}
                  of <span className="font-bold text-gray-800">{filtered.length}</span> entries
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition"
                  >
                    Previous
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                    .map((p, idx, arr) => {
                      const prevVal = arr[idx - 1];
                      return (
                        <React.Fragment key={p}>
                          {prevVal && p - prevVal > 1 && <span className="px-1 text-gray-400">...</span>}
                          <button
                            onClick={() => setCurrentPage(p)}
                            className={`px-3 py-1.5 rounded-lg font-bold transition cursor-pointer ${
                              currentPage === p
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {p}
                          </button>
                        </React.Fragment>
                      );
                    })}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ==================== FORM VIEW ==================== */
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="border-b border-gray-200 pb-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {editingId ? 'Edit Customer Rate Matrix' : 'Create Customer Rate Matrix'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Set customer specific tariff, route locations, minimums and breakdown surcharges
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowList(true);
                }}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* SECTION 1: Customer & Route Selection (Searchable React-Select) */}
            <div className="bg-gray-50/70 p-5 rounded-xl border border-gray-200">
              <h4 className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-4 flex items-center gap-1.5">
                <MdLocationOn size={16} /> Customer & Route Definitions
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Searchable Customer */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Customer *
                  </label>
                  <Select
                    placeholder="Search or Select Customer..."
                    options={customers.map((c) => ({ value: c._id, label: c.name }))}
                    value={
                      form.customerId
                        ? customers
                            .map((c) => ({ value: c._id, label: c.name }))
                            .find((opt) => opt.value === form.customerId) || null
                        : null
                    }
                    onChange={(opt) => setForm((prev) => ({ ...prev, customerId: opt ? opt.value : '' }))}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>

                {/* Searchable From State */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    From State *
                  </label>
                  <Select
                    placeholder="Search Origin State..."
                    options={states.map((s) => ({ value: s._id, label: s.statename }))}
                    value={
                      form.fromStateId
                        ? states
                            .map((s) => ({ value: s._id, label: s.statename }))
                            .find((opt) => opt.value === form.fromStateId) || null
                        : null
                    }
                    onChange={(opt) => setForm((prev) => ({ ...prev, fromStateId: opt ? opt.value : '' }))}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>

                {/* Searchable To State */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    To State *
                  </label>
                  <Select
                    placeholder="Search Destination State..."
                    options={states.map((s) => ({ value: s._id, label: s.statename }))}
                    value={
                      form.toStateId
                        ? states
                            .map((s) => ({ value: s._id, label: s.statename }))
                            .find((opt) => opt.value === form.toStateId) || null
                        : null
                    }
                    onChange={(opt) => setForm((prev) => ({ ...prev, toStateId: opt ? opt.value : '' }))}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>
              </div>

              {/* Location Selectors: From & To Areas with search and Select All */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5 pt-4 border-t border-gray-200">
                {/* From Locations */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-700 uppercase">
                      From Locations ({form.fromLocationId?.length || 0} Selected) *
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Search area..."
                        value={fromSearch}
                        onChange={(e) => setFromSearch(e.target.value)}
                        className="px-2.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => handleSelectAllAreas('from', filteredFromAreas)}
                        className="px-2.5 py-1 text-xs font-bold bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition cursor-pointer"
                      >
                        {filteredFromAreas.length > 0 &&
                        filteredFromAreas.every((item) => form.fromLocationId.includes(item._id))
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                    </div>
                  </div>

                  <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-white text-xs">
                    {!form.fromStateId ? (
                      <p className="text-gray-400 italic text-center py-6">Please select From State first</p>
                    ) : filteredFromAreas.length === 0 ? (
                      <p className="text-gray-400 italic text-center py-6">No areas match your search</p>
                    ) : (
                      filteredFromAreas.map((area) => (
                        <label
                          key={area._id}
                          className="flex items-center py-1.5 px-2 hover:bg-blue-50 rounded cursor-pointer transition"
                        >
                          <input
                            type="checkbox"
                            name="fromLocationId"
                            value={area._id}
                            checked={form.fromLocationId.includes(area._id)}
                            onChange={handleChange}
                            className="mr-2.5 h-4 w-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                          />
                          <span className="text-gray-800">{area.displayName}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* To Locations */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-700 uppercase">
                      To Locations ({form.toLocationId?.length || 0} Selected) *
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Search area..."
                        value={toSearch}
                        onChange={(e) => setToSearch(e.target.value)}
                        className="px-2.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => handleSelectAllAreas('to', filteredToAreas)}
                        className="px-2.5 py-1 text-xs font-bold bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition cursor-pointer"
                      >
                        {filteredToAreas.length > 0 &&
                        filteredToAreas.every((item) => form.toLocationId.includes(item._id))
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                    </div>
                  </div>

                  <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-white text-xs">
                    {!form.toStateId ? (
                      <p className="text-gray-400 italic text-center py-6">Please select To State first</p>
                    ) : filteredToAreas.length === 0 ? (
                      <p className="text-gray-400 italic text-center py-6">No areas match your search</p>
                    ) : (
                      filteredToAreas.map((area) => (
                        <label
                          key={area._id}
                          className="flex items-center py-1.5 px-2 hover:bg-blue-50 rounded cursor-pointer transition"
                        >
                          <input
                            type="checkbox"
                            name="toLocationId"
                            value={area._id}
                            checked={form.toLocationId.includes(area._id)}
                            onChange={handleChange}
                            className="mr-2.5 h-4 w-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                          />
                          <span className="text-gray-800">{area.displayName}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 2: Transport Mode & Route Classification */}
            <div className="bg-gray-50/70 p-5 rounded-xl border border-gray-200">
              <h4 className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-4 flex items-center gap-1.5">
                <MdDirectionsTransit size={16} /> Mode & Route Classification
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Transport Mode
                  </label>
                  <select
                    name="modeId"
                    value={form.modeId}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    {MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Metro Route
                  </label>
                  <select
                    name="isMetro"
                    value={form.isMetro}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value={0}>No</option>
                    <option value={1}>Yes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    ODA Route
                  </label>
                  <select
                    name="isOda"
                    value={form.isOda}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value={0}>No</option>
                    <option value={1}>Yes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value === 'true' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* SECTION 3: Primary Rates & Weight Thresholds */}
            <div className="bg-gray-50/70 p-5 rounded-xl border border-gray-200">
              <h4 className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-4 flex items-center gap-1.5">
                <HiOutlineScale size={16} /> Tariff Rates & Weight Thresholds
              </h4>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Rate / Kg (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="ratePerKg"
                    value={form.ratePerKg}
                    onChange={handleChange}
                    required
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none font-bold text-emerald-700"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Min Amount (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="minAmount"
                    value={form.minAmount}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Min Weight (Kg)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="minKgs"
                    value={form.minKgs}
                    onChange={handleChange}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Max Weight (Kg)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="maxKgs"
                    value={form.maxKgs}
                    onChange={handleChange}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Min Charge Wt
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="minChrgWeight"
                    value={form.minChrgWeight}
                    onChange={handleChange}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Round Off Wt
                  </label>
                  <input
                    type="number"
                    step="1"
                    name="adjWeight"
                    value={form.adjWeight}
                    onChange={handleChange}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* SECTION 4: Surcharges & Extra Breakdown Charges */}
            <div className="bg-gray-50/70 p-5 rounded-xl border border-gray-200">
              <h4 className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-4 flex items-center gap-1.5">
                <MdAttachMoney size={16} /> Additional Surcharges & Fees (₹)
              </h4>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[
                  { name: 'adRate', label: 'Ad Rate' },
                  { name: 'pickupOda', label: 'Pickup ODA' },
                  { name: 'deliveryOda', label: 'Delivery ODA' },
                  { name: 'pickupCharge', label: 'Pickup Chrg' },
                  { name: 'deliveryCharge', label: 'Delivery Chrg' },
                  { name: 'codChrg', label: 'COD Chrg' },
                  { name: 'docChrg', label: 'Doc Chrg' },
                  { name: 'fuelChrg', label: 'Fuel Chrg' },
                  { name: 'fovPercent', label: 'FOV %' },
                  { name: 'hamaliChrg', label: 'Hamali Chrg' },
                  { name: 'loadingCharge', label: 'Loading Chrg' },
                  { name: 'unloadingCharge', label: 'Unloading Chrg' },
                  { name: 'holidayCharges', label: 'Holiday Chrg' },
                  { name: 'specialCharges', label: 'Special Chrg' },
                  { name: 'cft', label: 'CFT Ratio' },
                  { name: 'iata', label: 'IATA Fee' },
                  { name: 'flatAmount', label: 'Flat Amount' },
                  { name: 'otherCharge', label: 'Other Chrg' },
                ].map((field) => (
                  <div key={field.name}>
                    <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1 truncate" title={field.label}>
                      {field.label}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name={field.name}
                      value={form[field.name]}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Form Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowList(true);
                }}
                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs uppercase rounded-lg transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase rounded-lg shadow-md transition disabled:opacity-60 cursor-pointer"
              >
                {loading ? 'Saving Tariff...' : editingId ? 'Update Rate' : 'Save Rate'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* VIEW DETAILS MODAL */}
      {viewDetailRecord && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white font-bold text-lg">
                  ₹
                </div>
                <div>
                  <h3 className="font-extrabold text-lg text-white">
                    {getCustomerName(viewDetailRecord)}
                  </h3>
                  <p className="text-xs text-blue-100">
                    Pricing matrix details & rate breakdown
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewDetailRecord(null)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition cursor-pointer"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* Status and Mode bar */}
              <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs">
                <div className="flex items-center gap-3">
                  <span className="font-bold text-gray-600 uppercase">Mode:</span>
                  <span className="font-bold text-blue-700 bg-blue-100 px-2.5 py-0.5 rounded uppercase">
                    {getModeLabel(viewDetailRecord.modeId)}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-gray-600 uppercase">Status:</span>
                  <span
                    className={`font-bold px-2.5 py-0.5 rounded-full ${
                      viewDetailRecord.status !== false
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {viewDetailRecord.status !== false ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>

              {/* Route Path Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/40">
                  <p className="text-xs font-bold text-blue-800 uppercase flex items-center gap-1.5 mb-1">
                    <MdLocationOn /> Origin Route
                  </p>
                  <p className="text-sm font-extrabold text-gray-900">
                    {getStateName(viewDetailRecord.fromStateId)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Mapped Locations: {Array.isArray(viewDetailRecord.fromLocationId) ? viewDetailRecord.fromLocationId.length : 0}
                  </p>
                </div>

                <div className="p-4 rounded-xl border border-indigo-100 bg-indigo-50/40">
                  <p className="text-xs font-bold text-indigo-800 uppercase flex items-center gap-1.5 mb-1">
                    <MdLocationOn /> Destination Route
                  </p>
                  <p className="text-sm font-extrabold text-gray-900">
                    {getStateName(viewDetailRecord.toStateId)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Mapped Locations: {Array.isArray(viewDetailRecord.toLocationId) ? viewDetailRecord.toLocationId.length : 0}
                  </p>
                </div>
              </div>

              {/* Base Tariffs */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-1">
                  <HiOutlineCurrencyRupee /> Core Rate Metrics
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="bg-emerald-50 p-2.5 rounded-lg border border-emerald-100">
                    <span className="text-[10px] uppercase font-bold text-emerald-700 block">Rate / Kg</span>
                    <span className="text-lg font-extrabold text-emerald-800">
                      ₹{viewDetailRecord.ratePerKg || 0}
                    </span>
                  </div>
                  <div className="bg-blue-50 p-2.5 rounded-lg border border-blue-100">
                    <span className="text-[10px] uppercase font-bold text-blue-700 block">Min Amount</span>
                    <span className="text-lg font-extrabold text-blue-800">
                      ₹{viewDetailRecord.minAmount || 0}
                    </span>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                    <span className="text-[10px] uppercase font-bold text-gray-500 block">Min Weight</span>
                    <span className="text-sm font-bold text-gray-800">
                      {viewDetailRecord.minKgs || 0} Kg
                    </span>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                    <span className="text-[10px] uppercase font-bold text-gray-500 block">Max Weight</span>
                    <span className="text-sm font-bold text-gray-800">
                      {viewDetailRecord.maxKgs || '∞'} Kg
                    </span>
                  </div>
                </div>
              </div>

              {/* Comprehensive Surcharge Breakdown */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-1">
                  <MdAttachMoney /> Surcharges & Extra Charges
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 text-xs">
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Pickup ODA:</span>
                    <span className="font-bold text-gray-800">₹{viewDetailRecord.pickupOda || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Delivery ODA:</span>
                    <span className="font-bold text-gray-800">₹{viewDetailRecord.deliveryOda || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Pickup Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDetailRecord.pickupCharge || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Delivery Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDetailRecord.deliveryCharge || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Fuel Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDetailRecord.fuelChrg || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Doc Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDetailRecord.docChrg || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">FOV Percent:</span>
                    <span className="font-bold text-gray-800">{viewDetailRecord.fovPercent || 0}%</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Hamali Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDetailRecord.hamaliChrg || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Loading Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDetailRecord.loadingCharge || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">Unloading Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDetailRecord.unloadingCharge || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">COD Charge:</span>
                    <span className="font-bold text-gray-800">₹{viewDetailRecord.codChrg || 0}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <span className="text-gray-500 block text-[10px]">CFT Ratio:</span>
                    <span className="font-bold text-gray-800">{viewDetailRecord.cft || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200 rounded-b-2xl">
              <button
                onClick={() => {
                  const rec = viewDetailRecord;
                  setViewDetailRecord(null);
                  handleEdit(rec);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold uppercase transition flex items-center gap-1.5 cursor-pointer"
              >
                <MdEdit size={16} /> Edit Rate Matrix
              </button>
              <button
                onClick={() => setViewDetailRecord(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-bold uppercase transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPriceSetting;